// STARCODE particle engine core: owns the GPGPU simulation (position + velocity textures
// updated entirely on the GPU) and the Points render material that visualizes it. This is the
// piece that gives "geilere Performance" versus the CPU-bound Processing sketch - tens of
// thousands of particles are simulated in a handful of full-screen shader passes instead of a
// per-particle JavaScript/Java loop.

import * as THREE from "three";
import { GPUComputationRenderer } from "three/examples/jsm/misc/GPUComputationRenderer.js";

import noiseGLSL from "./gpgpu/noise.glsl?raw";
import velocityShaderGLSL from "./gpgpu/velocityShader.glsl?raw";
import positionShaderGLSL from "./gpgpu/positionShader.glsl?raw";
import pointsVertexGLSL from "./gpgpu/pointsVertex.glsl?raw";
import pointsFragmentGLSL from "./gpgpu/pointsFragment.glsl?raw";

import { buildTextTargets } from "./textTarget.js";
import { buildDefault3DMeshTargets, parseObjTargets } from "./objTarget.js";
import { state } from "./state.js";
import { t, getLang } from "./i18n.js";

// Second text morph target used as an on-screen credit/branding during a live set.
export const BRAND_WORD = "STUDIOPESC.COM";

// Particle presets: switchable on the fly without page reload
export const PARTICLE_PRESETS = [
  { count: 16384, size: 128, label: "16.384 (Eco)" },
  { count: 36864, size: 192, label: "36.864 (Low)" },
  { count: 65536, size: 256, label: "65.536 (Standard)" },
  { count: 147456, size: 384, label: "147.456 (High)" },
  { count: 262144, size: 512, label: "262.144 (Ultra 4K)" },
  { count: 272144, size: 1024, label: "272.144 (Default)" },
  { count: 524288, size: 1024, label: "524.288 (Extreme)" },
  { count: 1048576, size: 1024, label: "1.048.576 (Maximum)" },
];

export const MAX_TEX_SIZE = 1024;
export const MAX_PARTICLE_COUNT = MAX_TEX_SIZE * MAX_TEX_SIZE;

export function getInitialParticleCount() {
  if (typeof window === "undefined") return 272144;
  const urlParam = new URLSearchParams(window.location.search).get("particles");
  const count = parseInt(urlParam, 10);
  const preset = PARTICLE_PRESETS.find((p) => p.count === count);
  return preset ? preset.count : 272144;
}

export const PARTICLE_COUNT = getInitialParticleCount();
export const TEX_SIZE = MAX_TEX_SIZE;

export const BOUNDS_RADIUS = 420;

// Maps a themed effect's state flag name to a target HSL palette. Rather than hardcoding dozens
// of per-effect color branches into the fragment shader, every "thematic" ported effect (nebula,
// ember wind, ink bleed, galaxy family, liquid family, ...) just gets an entry here and the
// fragment shader blends toward it generically via uThemeHue/Sat/Light/Blend.
const THEME_COLORS = {
  swarmOn: { h: 0.33, s: 0.5, l: 0.55 },
  vortexOn: { h: 0.66, s: 0.6, l: 0.5 },
  galaxySpiralOn: { h: 0.7, s: 0.55, l: 0.55 }, // Galaxy 01 - Kepler Differential Spiral
  galaxyDiscOn: { h: 0.58, s: 0.45, l: 0.55 }, // Galaxy 02 - 3-Arm Flat Spiral Disc
  cosmicGalaxyOn: { h: 0.72, s: 0.92, l: 0.62 }, // Galaxy 03 - Realistic Logarithmic Multi-Arm Spiral Galaxy
  galaxy04On: { h: 0.58, s: 0.96, l: 0.64 }, // Galaxy 04 - Grand-Design Barred Spiral Galaxy (Image 1: Golden Core & Sapphire Arms)
  ringNebulaOn: { h: 0.08, s: 0.98, l: 0.68 }, // Ring Nebula M57 (Image 2: Glowing Cyan Cavity & Radiant Amber Outer Ring)
  ringOn: { h: 0.48, s: 0.95, l: 0.65 }, // Electric cyan-aquamarine acoustic ring
  blackHoleOn: { h: 0.08, s: 0.95, l: 0.58 }, // blazing amber/orange relativistic accretion disk
  neutronStarOn: { h: 0.58, s: 0.92, l: 0.65 }, // electric pulsar cyan-blue & violet plasma
  sunOn: { h: 0.09, s: 0.98, l: 0.66 }, // radiant solar white-gold & amber corona
  fireballOn: { h: 0.09, s: 0.98, l: 0.66 },
  liquidRippleOn: { h: 0.5, s: 0.6, l: 0.55 },
  inkBleedOn: { h: 0.55, s: 0.75, l: 0.28 },
  portalOn: { h: 0.78, s: 0.65, l: 0.5 },
  causticsOn: { h: 0.47, s: 0.5, l: 0.65 },
  redCircleOn: { h: 0.0, s: 0.85, l: 0.5 },
  sphereOn: { h: 0.52, s: 0.65, l: 0.62 }, // luminous cyan-aquamarine planetary sphere
  crystalOn: { h: 0.52, s: 0.85, l: 0.72 }, // diamond/ice crystalline light
  hexMatrixOn: { h: 0.48, s: 0.95, l: 0.65 }, // geometric cyan/emerald honeycomb matrix
  fibonacciGridOn: { h: 0.12, s: 0.95, l: 0.65 }, // golden phyllotaxis spiral shell
  fireflyRiverOn: { h: 0.22, s: 0.95, l: 0.65 }, // glowing firefly amber-gold & emerald phosphorescent river streams
  bioCellOn: { h: 0.38, s: 0.85, l: 0.55 }, // emerald/lime bioluminescent cell
  bioSynapseOn: { h: 0.46, s: 0.95, l: 0.62 }, // deep-sea synaptic neural phosphorescence
  spectralCathedralOn: { h: 0.54, s: 0.92, l: 0.68 }, // cyan-blue spectral architecture
  pulseBloomOn: { h: 0.92, s: 0.9, l: 0.64 }, // magenta-gold kinetic flower
  electroOn: { h: 0.56, s: 0.95, l: 0.65 }, // neon cyan-blue lightning discharge
  starSkyOn: { h: 0.62, s: 0.82, l: 0.70 }, // deep cosmic midnight blue & silver stars
  antimatterCollisionOn: { h: 0.95, s: 0.95, l: 0.65 }, // crimson/red annihilation
  cryoLatticeOn: { h: 0.50, s: 0.80, l: 0.80 }, // frosty crystal blue-white
  hyperdriveWarpOn: { h: 0.62, s: 0.85, l: 0.70 }, // electric hyperdrive blue
  // Cool, desaturated cyan-white - reads as "rigid/technical" rather than organic, matching the
  // stiff square grid formation itself.
  gridPulseOn: { h: 0.55, s: 0.3, l: 0.65 },
};
const THEME_KEYS = Object.keys(THEME_COLORS);

function lerpHue(a, b, t) {
  let diff = ((b - a + 0.5) % 1) - 0.5;
  if (diff < -0.5) diff += 1;
  return ((a + diff * t) % 1 + 1) % 1;
}

export class ParticleSystem {
  constructor(renderer) {
    this.renderer = renderer;
    this.clockTime = 0;

    this.gpuCompute = new GPUComputationRenderer(MAX_TEX_SIZE, MAX_TEX_SIZE, renderer);

    const dtPosition = this.gpuCompute.createTexture();
    const dtVelocity = this.gpuCompute.createTexture();
    this._fillInitialTextures(dtPosition, dtVelocity);

    this.velocityVariable = this.gpuCompute.addVariable(
      "textureVelocity",
      noiseGLSL + "\n" + velocityShaderGLSL,
      dtVelocity
    );
    this.positionVariable = this.gpuCompute.addVariable(
      "texturePosition",
      positionShaderGLSL,
      dtPosition
    );

    this.gpuCompute.setVariableDependencies(this.velocityVariable, [this.positionVariable, this.velocityVariable]);
    this.gpuCompute.setVariableDependencies(this.positionVariable, [this.positionVariable, this.velocityVariable]);

    this.morphTargetTexture = new THREE.DataTexture(
      new Float32Array(MAX_PARTICLE_COUNT * 4),
      MAX_TEX_SIZE,
      MAX_TEX_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType
    );
    // Nearest filtering: each texel is one particle's exact designated target point - linear
    // interpolation would blend unrelated points on the letters together at the sample edges.
    this.morphTargetTexture.minFilter = THREE.NearestFilter;
    this.morphTargetTexture.magFilter = THREE.NearestFilter;
    this.morphTargetTexture.generateMipmaps = false;
    this.morphTargetTexture.needsUpdate = true;

    const vUniforms = this.velocityVariable.material.uniforms;
    Object.assign(vUniforms, {
      uTime: { value: 0 },
      uDelta: { value: 0 },
      uAudioLow: { value: 0 },
      uAudioMid: { value: 0 },
      uAudioHigh: { value: 0 },
      uAudioSub: { value: 0 },
      uAudioBass: { value: 0 },
      uAudioLowMid: { value: 0 },
      uAudioHighMid: { value: 0 },
      uAudioTreble: { value: 0 },
      uFlowStrength: { value: 6.5 },
      uBoundsRadius: { value: BOUNDS_RADIUS },
      uGravityOn: { value: 0 },
      uGravityStrength: { value: 70 },
      uSunOn: { value: 0 },
      uSunScale: { value: 0 },
      uSunStrength: { value: 65 },
      uFireballOn: { value: 0 },
      uFireballIntensity: { value: 55 },
      uFireballTurbulence: { value: 40 },
      uFireballCenter: { value: new THREE.Vector3(0, 0, 0) },
      uFireballCoreRadius: { value: BOUNDS_RADIUS * 0.16 },
      uFireballInfluenceRadius: { value: BOUNDS_RADIUS * 0.42 },
      uFireballOuterMultiplier: { value: 2.2 },
      uRingOn: { value: 0 },
      uRingIntensity: { value: 45 },
      uRingRadius: { value: BOUNDS_RADIUS * 0.55 },
      uRingThickness: { value: BOUNDS_RADIUS * 0.14 },
      uRippleOn: { value: 0 },
      uRippleRadius: { value: 0 },
      uRippleWidth: { value: 70 },
      uRippleStrength: { value: 60 },
      uExplodeTime: { value: -999 },
      uExplodeStrength: { value: 0 },
      uRbBurstTime: { value: -999 },
      uRbBurstStrength: { value: 0 },
      uShockwaveTime: { value: -999 },
      uShockwaveStrength: { value: 0 },

      // --- Audio-Reactivity switches ---
      uAudioReactGlobal: { value: 1.0 },
      uAudioReactAttractors: { value: 1.0 },
      uAudioReactBasis: { value: 1.0 },
      uAudioReactEffects: { value: 1.0 },
      uAudioReactExtended: { value: 1.0 },

      // --- Restored NEU.pde controls: held A/S attractors + numeric-key audio-reactive effects ---
      uAttractorNegOn: { value: 0 },
      uAttractorPosOn: { value: 0 },
      uAttractorStrength: { value: 380 },
      uSpiralHighOn: { value: 0 },
      uBandExplodeOn: { value: 0 },
      uShimmerOn: { value: 0 },
      uComplexOn: { value: 0 },

      // --- Extended effect set ---
      uWellsOn: { value: 0 },
      uWellsStrength: { value: 55 },
      uWellsRadius: { value: BOUNDS_RADIUS * 0.5 },
      uGalaxyDiscOn: { value: 0 },
      uGalaxyDiscStrength: { value: 60 },
      uSwarmOn: { value: 0 },
      uSwarmStrength: { value: 50 },
      uVortexOn: { value: 0 },
      uVortexStrength: { value: 65 },
      uGalaxySpiralOn: { value: 0 },
      uGalaxySpiralStrength: { value: 60 },
      uCosmicGalaxyOn: { value: 0 },
      uCosmicGalaxyStrength: { value: 65 },
      uBlackHoleOn: { value: 0 },
      uBlackHoleStrength: { value: 65 },
      uNeutronStarOn: { value: 0 },
      uNeutronStarStrength: { value: 65 },
      uLiquidRippleOn: { value: 0 },
      uLiquidRippleStrength: { value: 55 },
      uInkBleedOn: { value: 0 },
      uInkBleedStrength: { value: 50 },
      uPortalOn: { value: 0 },
      uPortalStrength: { value: 60 },
      uCausticsOn: { value: 0 },
      uCausticsStrength: { value: 40 },
      uChaosBoost: { value: 1.0 },
      uDistortionOn: { value: 0 },
      uDistortionStrength: { value: 45 },
      uFractalOn: { value: 0 },
      uFractalStrength: { value: 50 },
      uPatternOn: { value: 0 },
      uPatternStrength: { value: 60 },
      uRedCircleOn: { value: 0 },
      uRedCircleStrength: { value: 60 },
      uShapeChangeOn: { value: 0 },
      uShapeChangeStrength: { value: 60 },
      uStreamsOn: { value: 0 },
      uGridPulseOn: { value: 0 },
      uGridPulseStrength: { value: 65 },
      uBlackHoleOn: { value: 0 },
      uBlackHoleStrength: { value: 65 },
      uNeutronStarOn: { value: 0 },
      uNeutronStarStrength: { value: 65 },
      uSphereOn: { value: 0 },
      uSphereStrength: { value: 65 },

      uGalaxy04On: { value: 0 },
      uGalaxy04Strength: { value: 65 },
      uRingNebulaOn: { value: 0 },
      uRingNebulaStrength: { value: 65 },

      uCrystalOn: { value: 0 },
      uCrystalStrength: { value: 65 },
      uHexMatrixOn: { value: 0 },
      uHexMatrixStrength: { value: 65 },
      uFibonacciGridOn: { value: 0 },
      uFibonacciGridStrength: { value: 65 },
      uFireflyRiverOn: { value: 0 },
      uFireflyRiverStrength: { value: 65 },
      uBioCellOn: { value: 0 },
      uBioCellStrength: { value: 65 },
      uBioSynapseOn: { value: 0 },

      uAntimatterCollisionOn: { value: 0 },
      uCryoLatticeOn: { value: 0 },
      uHyperdriveWarpOn: { value: 0 },

      uObjDef1On: { value: 0 },
      uObjDef2On: { value: 0 },
      uObjDef3On: { value: 0 },
      uElectroOn: { value: 0 },
      uStarSkyOn: { value: 0 },

      uMorphOn: { value: 0 },
      uMorphElapsed: { value: 0 },
      uMorphStagger: { value: 0.9 },
      uMorphRamp: { value: 1.1 },
      uMorphTargetTexture: { value: this.morphTargetTexture },
      uMaxSpeed: { value: 150 },
      uSlowMotion: { value: 1 },
      uShockwaveAudioOn: { value: 0 },

      // --- Global live-performance parameters + new audio-reactive motion effects ---
      uMasterIntensity: { value: 1.0 },
      uWrapOn: { value: 0 },
      uBeatPulseOn: { value: 0 },
      uBeatPulse: { value: 0 },
      uBeatTime: { value: -999 },
      uBreathOn: { value: 0 },
      uTunnelOn: { value: 0 },
      uHelixOn: { value: 0 },
      uStrobeFieldOn: { value: 0 },
      uSpectralCathedralOn: { value: 0 },
      uPulseBloomOn: { value: 0 },
      uAudioFlux: { value: 0 },
    });

    const pUniforms = this.positionVariable.material.uniforms;
    Object.assign(pUniforms, {
      uDelta: { value: 0 },
      uSlowMotion: { value: 1 },
      uWrapOn: { value: 0 },
      // Half-extent of the wrap box. Slightly larger than BOUNDS_RADIUS so the seam sits just
      // outside the usual swarm volume and particles wrap at the edge of frame, not mid-cloud.
      uWrapSize: { value: BOUNDS_RADIUS * 1.15 },
    });

    this.velocityVariable.wrapS = this.velocityVariable.wrapT = THREE.RepeatWrapping;
    this.positionVariable.wrapS = this.positionVariable.wrapT = THREE.RepeatWrapping;

    const error = this.gpuCompute.init();
    if (error !== null) {
      console.error("[STARCODE] GPUComputationRenderer init error:", error);
    }

    this._buildPoints();

    this._ripplePhaseTime = -999;
    this._morphReady = false;
    this._starcodeTargets = null;
    this._brandTargets = null;
    this._objTargets = null;
    this._customObjTargets = null;
    this._currentMorphType = "none";
    this._themeHue = 0.56;
    this._themeBlend = 0;
    this._sunScale = 0.0;
    this._lastBeatTime = -999;
    this._shockwaveTime = -999;
    this._shockwaveStrength = 0;
    this._swarmCenter = new THREE.Vector3(0, 0, 0);
    this._readBuffer = null;
    this._effectWeights = {};
    this._smoothedMorphWeight = 0.0;

    // Startup intro: STARCODE text stands clearly formed & legible for ~0.5s facing camera, then dissolves smoothly into the swarm
    this._introT0 = null;
    this._introDone = true;
    this._introReleased = false;
    this._prevMorphing = false;
    this._morphSwitchTime = -999;

    // Pre-load STARCODE text targets immediately on creation
    this.loadMorphTarget("STARCODE");
  }

  isIntroRunning() {
    return !this._introDone && this._introT0 !== null;
  }

  _getSmoothedWeight(key, dt, targetOverride = null, easeIn = 3.5, easeOut = 2.2) {
    if (!this._effectWeights) this._effectWeights = {};
    const target = targetOverride !== null ? targetOverride : (state[key] ? 1.0 : 0.0);
    const current = this._effectWeights[key] ?? target;
    const rate = target > current ? easeIn : easeOut;
    const updated = current + (target - current) * Math.min(1.0, dt * rate);
    this._effectWeights[key] = updated;
    return updated;
  }

  // Scripted startup sequence:
  // Starts dissolving immediately upon loading (already ~10% in progress from frame 1)
  // and smoothly flows into the organic particle swarm over ~2.4s.
  _updateIntro(userMorphing) {
    if (this._introDone || this._introT0 === null) return null;
    // A manual morph always wins - cancel the intro immediately.
    if (userMorphing) {
      this._introDone = true;
      return null;
    }

    const INTRO_OFF = 2.4;      // smooth gradual dissolve duration

    const off = this.clockTime - this._introT0;
    if (!this._introReleased) {
      this._introReleased = true;
      // Gentle kinetic scatter burst into the flowing swarm
      this.triggerExplosion(36);
    }
    if (off >= INTRO_OFF) {
      this._introDone = true;
      return null;
    }
    return { on: false, elapsed: off };
  }

  _fillInitialTextures(dtPosition, dtVelocity) {
    const posArr = dtPosition.image.data;
    const velArr = dtVelocity.image.data;
    for (let i = 0; i < MAX_PARTICLE_COUNT; i++) {
      const i4 = i * 4;
      // Scatter inside a soft sphere so the idle swarm reads as a volumetric cloud from frame one.
      const r = BOUNDS_RADIUS * 0.55 * Math.cbrt(Math.random());
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      posArr[i4 + 0] = r * Math.sin(phi) * Math.cos(theta);
      posArr[i4 + 1] = r * Math.sin(phi) * Math.sin(theta);
      posArr[i4 + 2] = r * Math.cos(phi);
      posArr[i4 + 3] = Math.random(); // per-particle seed, used everywhere for jitter/hue/stagger

      velArr[i4 + 0] = (Math.random() - 0.5) * 4;
      velArr[i4 + 1] = (Math.random() - 0.5) * 4;
      velArr[i4 + 2] = (Math.random() - 0.5) * 4;
      velArr[i4 + 3] = 0;
    }
  }

  _buildPoints() {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(MAX_PARTICLE_COUNT * 3);
    const reference = new Float32Array(MAX_PARTICLE_COUNT * 2);

    for (let idx = 0; idx < MAX_PARTICLE_COUNT; idx++) {
      const i = idx % MAX_TEX_SIZE;
      const j = Math.floor(idx / MAX_TEX_SIZE);
      reference[idx * 2 + 0] = (i + 0.5) / MAX_TEX_SIZE;
      reference[idx * 2 + 1] = (j + 0.5) / MAX_TEX_SIZE;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("reference", new THREE.BufferAttribute(reference, 2));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), BOUNDS_RADIUS * 3);
    this.activeParticleCount = PARTICLE_COUNT;
    geometry.setDrawRange(0, this.activeParticleCount);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        texturePosition: { value: null },
        textureVelocity: { value: null },
        uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
        uBaseSize: { value: 3.6 },
        uSizeMultiplier: { value: 1.0 },
        uMorphOn: { value: 0 },
        uMorphElapsed: { value: 0 },
        uMorphStagger: { value: 0.9 },
        uMorphRamp: { value: 1.1 },
        uAudioHigh: { value: 0 },
        uAudioLow: { value: 0 },
        uAudioMid: { value: 0 },
        uTime: { value: 0 },
        uHueBase: { value: 0.56 },
        uOpacity: { value: 0.95 },
        uScalePulseOn: { value: 0 },
        uThemeHue: { value: 0.56 },
        uThemeSat: { value: 0.55 },
        uThemeLight: { value: 0.55 },
        uThemeBlend: { value: 0 },
        uColorInvert: { value: 0 },
        uPermanentColor: { value: 0 },
        uStarSkyOn: { value: 0 },
        uShapeChangeOn: { value: 0 },
        uShimmerOn: { value: 0 },

        // --- New audio-reactive colour/size effects + global live parameters ---
        uBioluminescenceOn: { value: 0 },
        uBioIntensity: { value: 0.85 },
        uBioSpeed: { value: 0.55 },
        uBioSynapseOn: { value: 0 },
        uBioSynapseIntensity: { value: 1.0 },
        uSpectrumColorOn: { value: 0 },
        uChromaWaveOn: { value: 0 },
        uBeatFlashOn: { value: 0 },
        uAudioSizeOn: { value: 0 },
        uBeatPulse: { value: 0 },
        uAudioFlux: { value: 0 },
        uHueShift: { value: 0 },
        uSizeBoost: { value: 1 },
        uBoundsRadius: { value: BOUNDS_RADIUS },
        uAudioSub: { value: 0 },
        uAudioBass: { value: 0 },
        uAudioLowMid: { value: 0 },
        uAudioHighMid: { value: 0 },
        uAudioTreble: { value: 0 },
        uFocusDistance: { value: 780 },
        uDofBlur: { value: 0.45 },
        uDofInvert: { value: 0 },
        uDofAudioReact: { value: 0 },
        uSunScale: { value: 0 },
        uShockwaveTime: { value: -999 },
        uShockwaveStrength: { value: 0 },
        uShockwaveAudioOn: { value: 0 },
        uGalaxy04On: { value: 0 },
        uRingNebulaOn: { value: 0 },
        uObjDef1On: { value: 0 },
        uObjDef2On: { value: 0 },
        uObjDef3On: { value: 0 },
        uElectroOn: { value: 0 },
      },
      vertexShader: pointsVertexGLSL,
      fragmentShader: pointsFragmentGLSL,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this.points = new THREE.Points(geometry, this.material);
    this.points.frustumCulled = false;
  }

  setParticleCount(newCount) {
    const count = Math.max(1024, Math.min(newCount, MAX_PARTICLE_COUNT));
    this.activeParticleCount = count;
    state.particleCount = count;
    localStorage.setItem("starcode_particle_count", String(count));
    if (this.points?.geometry) {
      this.points.geometry.setDrawRange(0, count);
    }
  }

  resetSwarm() {
    // Re-seeds the GPGPU position & velocity textures into a fresh, clean volumetric cloud
    this._introDone = true;
    this._introT0 = null;
    this._effectWeights = {};
    this._ripplePhaseTime = -999;
    this._lastBeatTime = -999;
    this._shockwaveTime = -999;
    this._shockwaveStrength = 0;
    this._sunScale = 0;
    const initPos = this.gpuCompute.createTexture();
    const posArr = initPos.image.data;
    const initVel = this.gpuCompute.createTexture();
    const velArr = initVel.image.data;

    for (let i = 0; i < MAX_PARTICLE_COUNT; i++) {
      const i4 = i * 4;
      const r = BOUNDS_RADIUS * 0.55 * Math.cbrt(Math.random());
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      posArr[i4 + 0] = r * Math.sin(phi) * Math.cos(theta);
      posArr[i4 + 1] = r * Math.sin(phi) * Math.sin(theta);
      posArr[i4 + 2] = r * Math.cos(phi);
      posArr[i4 + 3] = Math.random();

      velArr[i4 + 0] = (Math.random() - 0.5) * 4.0;
      velArr[i4 + 1] = (Math.random() - 0.5) * 4.0;
      velArr[i4 + 2] = (Math.random() - 0.5) * 4.0;
      velArr[i4 + 3] = 0;
    }

    this.gpuCompute.renderTexture(initPos, this.positionVariable.renderTargets[0]);
    this.gpuCompute.renderTexture(initPos, this.positionVariable.renderTargets[1]);
    this.gpuCompute.renderTexture(initVel, this.velocityVariable.renderTargets[0]);
    this.gpuCompute.renderTexture(initVel, this.velocityVariable.renderTargets[1]);
  }

  async loadMorphTarget(word) {
    if (!this._starcodeTargets) {
      this._starcodeTargets = await buildTextTargets(word, MAX_PARTICLE_COUNT);
      this._objTargets = buildDefault3DMeshTargets(MAX_PARTICLE_COUNT);
      this._customObjTargets = null;
    }
    this.morphTargetTexture.image.data.set(this._starcodeTargets);
    this.morphTargetTexture.needsUpdate = true;
    this._morphReady = true;

    // Branding lettering is built in the background so the first click on the STUDIOPESC.COM
    // button is instant. It must not delay the startup intro, hence no await here. The word is
    // 14 characters against STARCODE's 8, so it gets a proportionally wider target box - that
    // keeps the individual glyphs the same size and therefore just as readable.
    buildTextTargets(BRAND_WORD, MAX_PARTICLE_COUNT, { targetWidth: 920, offsetY: 60 })
      .then((targets) => { this._brandTargets = targets; })
      .catch((err) => console.error("[STARCODE] Branding-Morph konnte nicht gebaut werden:", err));

    this.startIntro();
  }

  async loadCustomWord(word) {
    if (!word || !word.trim()) return;
    const cleanWord = word.trim().slice(0, 10).toUpperCase();
    try {
      const targets = await buildTextTargets(cleanWord, MAX_PARTICLE_COUNT, { targetWidth: 560, offsetY: 40 });
      this._starcodeTargets = targets;
      this.morphTargetTexture.image.data.set(targets);
      this.morphTargetTexture.needsUpdate = true;
      state.morphOn = true;
      state.objMorphOn = false;
      state.brandMorphOn = false;
      state.morphToggleTime = performance.now() / 1000;
      return true;
    } catch (err) {
      console.error("[STARCODE] Custom Word Morph error:", err);
      return false;
    }
  }

  startIntro() {
    this._introDone = true;
    this._introT0 = null;
    const initPos = this.gpuCompute.createTexture();
    const posArr = initPos.image.data;
    const initVel = this.gpuCompute.createTexture();
    const velArr = initVel.image.data;

    // Direct initialization into the STARCODE lettering targets with ~10% already in motion
    if (this._starcodeTargets) {
      this.morphTargetTexture.image.data.set(this._starcodeTargets);
      this.morphTargetTexture.needsUpdate = true;
      for (let i = 0; i < MAX_PARTICLE_COUNT; i++) {
        const i4 = i * 4;
        const seed = (i * 0.618033988749895) % 1.0;
        posArr[i4 + 0] = this._starcodeTargets[i4 + 0];
        posArr[i4 + 1] = this._starcodeTargets[i4 + 1];
        posArr[i4 + 2] = this._starcodeTargets[i4 + 2];
        posArr[i4 + 3] = seed;

        if (seed < 0.12) {
          // ~10-12% of particles already gently dispersing on boot
          posArr[i4 + 0] += (Math.random() - 0.5) * 16.0;
          posArr[i4 + 1] += (Math.random() - 0.5) * 16.0;
          posArr[i4 + 2] += (Math.random() - 0.5) * 16.0;
          velArr[i4 + 0] = (Math.random() - 0.5) * 20.0;
          velArr[i4 + 1] = (Math.random() - 0.5) * 20.0;
          velArr[i4 + 2] = (Math.random() - 0.5) * 20.0;
        } else {
          velArr[i4 + 0] = 0;
          velArr[i4 + 1] = 0;
          velArr[i4 + 2] = 0;
        }
        velArr[i4 + 3] = 0;
      }
    } else {
      for (let i = 0; i < MAX_PARTICLE_COUNT; i++) {
        const i4 = i * 4;
        posArr[i4 + 0] = (Math.random() - 0.5) * 100;
        posArr[i4 + 1] = (Math.random() - 0.5) * 60;
        posArr[i4 + 2] = (Math.random() - 0.5) * 40;
        posArr[i4 + 3] = (i * 0.618033988749895) % 1.0;

        velArr[i4 + 0] = (Math.random() - 0.5) * 4.0;
        velArr[i4 + 1] = (Math.random() - 0.5) * 4.0;
        velArr[i4 + 2] = (Math.random() - 0.5) * 4.0;
        velArr[i4 + 3] = 0;
      }
    }

    this.gpuCompute.renderTexture(initPos, this.positionVariable.renderTargets[0]);
    this.gpuCompute.renderTexture(initPos, this.positionVariable.renderTargets[1]);
    this.gpuCompute.renderTexture(initVel, this.velocityVariable.renderTargets[0]);
    this.gpuCompute.renderTexture(initVel, this.velocityVariable.renderTargets[1]);

    // Offset by ~0.24s (~10% of 2.4s) so the dissolve is already underway from frame 1
    this._introT0 = this.clockTime - 0.24;
    this._introDone = false;
    this._introReleased = false;
  }

  loadCustomObj(objText, name = "3D-Modell") {
    try {
      const targets = parseObjTargets(objText, MAX_PARTICLE_COUNT);
      this._customObjTargets = targets;
      state.loadedObjName = name;
      if (state.objMorphOn) {
        this.morphTargetTexture.image.data.set(targets);
        this.morphTargetTexture.needsUpdate = true;
      }
      return { success: true, name };
    } catch (err) {
      console.error("[STARCODE] OBJ Parse Error:", err);
      return { success: false, error: err.message };
    }
  }

  triggerExplosion(strength = 80) {
    const u = this.velocityVariable.material.uniforms;
    u.uExplodeTime.value = this.clockTime;
    u.uExplodeStrength.value = strength;
  }

  triggerRbBurst(strength = 90) {
    const u = this.velocityVariable.material.uniforms;
    u.uRbBurstTime.value = this.clockTime;
    u.uRbBurstStrength.value = strength;
  }

  triggerShockwave(strength = 180) {
    this._shockwaveTime = this.clockTime;
    this._shockwaveStrength = strength;
    const u = this.velocityVariable.material.uniforms;
    u.uShockwaveTime.value = this.clockTime;
    u.uShockwaveStrength.value = strength;
  }

  setSizeMultiplier(v) {
    this.material.uniforms.uSizeMultiplier.value = v;
  }

  setSunScale(scale) {
    this._sunScale = scale;
  }

  update(dt, audio) {
    this.clockTime += dt;
    const vUniforms = this.velocityVariable.material.uniforms;
    const pUniforms = this.positionVariable.material.uniforms;

    // Global audio gain is applied here. Noise gate is already handled in AudioAnalyser.update().
    const gain = state.audioGain;
    const applyGain = (val) => Math.min(1.5, Math.max(0.0, val) * gain);

    const aLow = applyGain(audio.low);
    const aMid = applyGain(audio.mid);
    const aHigh = applyGain(audio.high);

    // Latch the timestamp of the most recent beat so the GPU shockwave can measure its own age.
    if ((audio.beat || audio.beatPulse > 0.85) && audio.level > 0.015) this._lastBeatTime = this.clockTime;

    // Combined low/mid/high energy (0..1-ish) - the GPU-port equivalent of NEU.pde's
    // rawAudioSignal(lowE, midE, highE), used by several of the restored numeric-key effects.
    const combinedSignal = (aLow + aMid + aHigh) / 3;

    // Slow Motion (Taste 1) - matches applySlowMotion()'s continuous formula: even at silence the
    // audio "amount" has a floor of 0.35 (some slow-down always applied while active), and it
    // deepens continuously with real audio energy - NOT the old hard binary 0.18 switch.
    let slow = 1.0;
    if (state.slowMotion) {
      const amount = Math.min(1, Math.max(0.35, combinedSignal));
      const factor = 1.0 - 0.85 * amount; // lerp(1.0, ~0.15, amount)
      slow = Math.min(1.0, Math.max(0.02, factor));
    }
    // Acceleration and position integration both consume uSlowMotion. Splitting the requested
    // time scale evenly keeps the combined response linear instead of accidentally squaring it
    // (0.40 used to become 0.16), which made formations appear inactive at the default speed.
    slow *= Math.sqrt(Math.max(0.001, state.speedScale));

    vUniforms.uTime.value = this.clockTime;
    vUniforms.uDelta.value = dt;
    vUniforms.uSlowMotion.value = slow;
    vUniforms.uAudioLow.value = aLow;
    vUniforms.uAudioMid.value = aMid;
    vUniforms.uAudioHigh.value = aHigh;
    vUniforms.uAudioSub.value = applyGain(audio.sub);
    vUniforms.uAudioBass.value = applyGain(audio.bass);
    vUniforms.uAudioLowMid.value = applyGain(audio.lowMid);
    vUniforms.uAudioHighMid.value = applyGain(audio.highMid);
    vUniforms.uAudioTreble.value = applyGain(audio.treble);
    vUniforms.uAudioFlux.value = audio.flux;
    vUniforms.uBeatPulse.value = audio.beatPulse;
    vUniforms.uBeatTime.value = this._lastBeatTime ?? -999;
    vUniforms.uMasterIntensity.value = state.masterIntensity;

    vUniforms.uBeatPulseOn.value = this._getSmoothedWeight("beatPulseOn", dt);
    vUniforms.uBreathOn.value = this._getSmoothedWeight("breathOn", dt);
    vUniforms.uTunnelOn.value = this._getSmoothedWeight("tunnelOn", dt);
    vUniforms.uHelixOn.value = this._getSmoothedWeight("helixOn", dt);
    vUniforms.uStrobeFieldOn.value = this._getSmoothedWeight("strobeFieldOn", dt);
    vUniforms.uSpectralCathedralOn.value = this._getSmoothedWeight("spectralCathedralOn", dt);
    vUniforms.uPulseBloomOn.value = this._getSmoothedWeight("pulseBloomOn", dt);

    // Audio-reactivity modulation switches per section + master.
    vUniforms.uAudioReactGlobal.value = state.audioReactGlobal ? 1 : 0;
    vUniforms.uAudioReactAttractors.value = state.audioReactAttractors ? 1 : 0;
    vUniforms.uAudioReactBasis.value = state.audioReactBasis ? 1 : 0;
    vUniforms.uAudioReactEffects.value = state.audioReactEffects ? 1 : 0;
    vUniforms.uAudioReactExtended.value = state.audioReactExtended ? 1 : 0;

    // Toroidal wrap mode - the velocity shader skips its inward bounds force while this is on.
    vUniforms.uWrapOn.value = state.wrapModeOn ? 1 : 0;
    pUniforms.uWrapOn.value = state.wrapModeOn ? 1 : 0;

    // Held A/S attractors - direct booleans set by keydown/keyup (see keybinds.js), not toggles.
    vUniforms.uAttractorNegOn.value = this._getSmoothedWeight("negAttractorHeld", dt, null, 14.0, 6.0);
    vUniforms.uAttractorPosOn.value = this._getSmoothedWeight("posAttractorHeld", dt, null, 14.0, 6.0);

    // Restored numeric-key audio-reactive effects (4, 5, 7, ß).
    vUniforms.uSpiralHighOn.value = this._getSmoothedWeight("spiralHighOn", dt);
    vUniforms.uBandExplodeOn.value = this._getSmoothedWeight("bandExplodeOn", dt);
    vUniforms.uShimmerOn.value = this._getSmoothedWeight("shimmerOn", dt);
    vUniforms.uComplexOn.value = this._getSmoothedWeight("complexOn", dt);

    vUniforms.uGravityOn.value = this._getSmoothedWeight("gravityOn", dt);
    const isSunActive = state.sunOn || state.fireballOn;
    const sunTarget = isSunActive ? 1.0 : 0.0;
    const sunSmoothing = Math.min(1, dt * (isSunActive ? 1.8 : 2.5));
    this._sunScale += (sunTarget - this._sunScale) * sunSmoothing;
    vUniforms.uSunOn.value = this._getSmoothedWeight("sunOn", dt);
    vUniforms.uSunScale.value = this._sunScale;
    this.material.uniforms.uSunScale.value = this._sunScale;
    vUniforms.uFireballOn.value = this._getSmoothedWeight("fireballOn", dt);
    vUniforms.uRingOn.value = this._getSmoothedWeight("ringOn", dt);
    vUniforms.uRippleOn.value = this._getSmoothedWeight("rippleOn", dt);

    // --- Extended effect set: push smoothed weights to uniforms every frame with easing. ---
    vUniforms.uWellsOn.value = this._getSmoothedWeight("wellsOn", dt);
    vUniforms.uGalaxySpiralOn.value = this._getSmoothedWeight("galaxySpiralOn", dt);
    vUniforms.uGalaxyDiscOn.value = this._getSmoothedWeight("galaxyDiscOn", dt);
    vUniforms.uCosmicGalaxyOn.value = this._getSmoothedWeight("cosmicGalaxyOn", dt);
    vUniforms.uGalaxy04On.value = this._getSmoothedWeight("galaxy04On", dt);
    vUniforms.uRingNebulaOn.value = this._getSmoothedWeight("ringNebulaOn", dt);
    vUniforms.uSwarmOn.value = this._getSmoothedWeight("swarmOn", dt);
    vUniforms.uVortexOn.value = this._getSmoothedWeight("vortexOn", dt);
    vUniforms.uBlackHoleOn.value = this._getSmoothedWeight("blackHoleOn", dt);
    vUniforms.uNeutronStarOn.value = this._getSmoothedWeight("neutronStarOn", dt);
    vUniforms.uLiquidRippleOn.value = this._getSmoothedWeight("liquidRippleOn", dt);
    vUniforms.uInkBleedOn.value = this._getSmoothedWeight("inkBleedOn", dt);
    vUniforms.uPortalOn.value = this._getSmoothedWeight("portalOn", dt);
    vUniforms.uCausticsOn.value = this._getSmoothedWeight("causticsOn", dt);
    vUniforms.uChaosBoost.value = this._getSmoothedWeight("chaosBoostOn", dt, state.chaosBoostOn ? 2.6 : 1.0);
    vUniforms.uDistortionOn.value = this._getSmoothedWeight("distortionOn", dt);
    vUniforms.uFractalOn.value = this._getSmoothedWeight("fractalOn", dt);
    vUniforms.uPatternOn.value = this._getSmoothedWeight("patternOn", dt);
    vUniforms.uRedCircleOn.value = this._getSmoothedWeight("redCircleOn", dt);
    vUniforms.uShapeChangeOn.value = this._getSmoothedWeight("shapeChangeOn", dt);
    vUniforms.uStreamsOn.value = this._getSmoothedWeight("streamsOn", dt);
    vUniforms.uGridPulseOn.value = this._getSmoothedWeight("gridPulseOn", dt);
    vUniforms.uSphereOn.value = this._getSmoothedWeight("sphereOn", dt);
    vUniforms.uCrystalOn.value = this._getSmoothedWeight("crystalOn", dt);
    vUniforms.uHexMatrixOn.value = this._getSmoothedWeight("hexMatrixOn", dt);
    vUniforms.uFibonacciGridOn.value = this._getSmoothedWeight("fibonacciGridOn", dt);
    vUniforms.uFireflyRiverOn.value = this._getSmoothedWeight("fireflyRiverOn", dt);
    vUniforms.uBioCellOn.value = this._getSmoothedWeight("bioCellOn", dt);
    vUniforms.uBioSynapseOn.value = this._getSmoothedWeight("bioSynapseOn", dt);
    vUniforms.uAntimatterCollisionOn.value = this._getSmoothedWeight("antimatterCollisionOn", dt);
    vUniforms.uCryoLatticeOn.value = this._getSmoothedWeight("cryoLatticeOn", dt);
    vUniforms.uHyperdriveWarpOn.value = this._getSmoothedWeight("hyperdriveWarpOn", dt);
    vUniforms.uObjDef1On.value = this._getSmoothedWeight("objDef1On", dt);
    vUniforms.uObjDef2On.value = this._getSmoothedWeight("objDef2On", dt);
    vUniforms.uObjDef3On.value = this._getSmoothedWeight("objDef3On", dt);
    vUniforms.uElectroOn.value = this._getSmoothedWeight("electroOn", dt);
    vUniforms.uStarSkyOn.value = this._getSmoothedWeight("starSkyOn", dt);

    // Themed color blend: find the currently active themed effect (if any) and smoothly blend
    // the render palette toward it; otherwise ease back to the default flow palette.
    let targetThemeKey = null;
    if (state.lastToggledEffect && state[state.lastToggledEffect] && THEME_COLORS[state.lastToggledEffect]) {
      targetThemeKey = state.lastToggledEffect;
    } else {
      targetThemeKey = THEME_KEYS.find((k) => state[k]) || null;
    }
    const themeSmoothing = Math.min(1, dt * 3.5);
    if (targetThemeKey) {
      const theme = THEME_COLORS[targetThemeKey];
      this._themeHue = lerpHue(this._themeHue, theme.h, themeSmoothing);
      this._themeBlend += (1 - this._themeBlend) * themeSmoothing;
      this._themeSat = (this._themeSat ?? theme.s) + (theme.s - (this._themeSat ?? theme.s)) * themeSmoothing;
      this._themeLight = (this._themeLight ?? theme.l) + (theme.l - (this._themeLight ?? theme.l)) * themeSmoothing;
    } else {
      this._themeBlend += (0 - this._themeBlend) * themeSmoothing;
    }
    this.material.uniforms.uThemeHue.value = this._themeHue;
    this.material.uniforms.uThemeSat.value = this._themeSat ?? 0.55;
    this.material.uniforms.uThemeLight.value = this._themeLight ?? 0.55;
    this.material.uniforms.uThemeBlend.value = this._themeBlend;

    this.material.uniforms.uScalePulseOn.value = this._getSmoothedWeight("scalePulseOn", dt);
    this.material.uniforms.uColorInvert.value = this._getSmoothedWeight("colorInvertOn", dt);
    this.material.uniforms.uPermanentColor.value = state.permanentColor;
    this.material.uniforms.uStarSkyOn.value = this._getSmoothedWeight("starSkyOn", dt);
    this.material.uniforms.uShapeChangeOn.value = this._getSmoothedWeight("shapeChangeOn", dt);
    this.material.uniforms.uShimmerOn.value = this._getSmoothedWeight("shimmerOn", dt);

    // New audio-reactive colour/size effects + global live parameters.
    this.material.uniforms.uBioluminescenceOn.value = this._getSmoothedWeight("bioluminescenceOn", dt);
    this.material.uniforms.uBioIntensity.value = state.bioIntensity ?? 0.85;
    this.material.uniforms.uBioSpeed.value = state.bioSpeed ?? 0.55;
    this.material.uniforms.uBioSynapseOn.value = this._getSmoothedWeight("bioSynapseOn", dt);
    this.material.uniforms.uBioSynapseIntensity.value = state.bioSynapseIntensity ?? 1.0;
    this.material.uniforms.uSpectrumColorOn.value = this._getSmoothedWeight("spectrumColorOn", dt);
    this.material.uniforms.uChromaWaveOn.value = this._getSmoothedWeight("chromaWaveOn", dt);
    this.material.uniforms.uBeatFlashOn.value = this._getSmoothedWeight("beatFlashOn", dt);
    this.material.uniforms.uAudioSizeOn.value = this._getSmoothedWeight("audioSizeOn", dt);
    this.material.uniforms.uBeatPulse.value = audio.beatPulse;
    this.material.uniforms.uAudioFlux.value = audio.flux;
    this.material.uniforms.uHueShift.value = state.hueShift;
    this.material.uniforms.uSizeBoost.value = state.sizeBoost;
    this.material.uniforms.uGalaxy04On.value = this._getSmoothedWeight("galaxy04On", dt);
    this.material.uniforms.uRingNebulaOn.value = this._getSmoothedWeight("ringNebulaOn", dt);
    this.material.uniforms.uObjDef1On.value = this._getSmoothedWeight("objDef1On", dt);
    this.material.uniforms.uObjDef2On.value = this._getSmoothedWeight("objDef2On", dt);
    this.material.uniforms.uObjDef3On.value = this._getSmoothedWeight("objDef3On", dt);
    this.material.uniforms.uElectroOn.value = this._getSmoothedWeight("electroOn", dt);

    // Bokeh / DOF Audio-Reactivity: shift focal plane dynamically in time with the beat rhythm
    let effectiveFocus = state.focusDistance ?? 780;
    if (state.dofAudioReact) {
      const beatFreq = audio.bpm && audio.bpm > 40 ? (audio.bpm / 60) * Math.PI : 3.2;
      const rhythmicOscillation = Math.sin(this.clockTime * beatFreq) * 240.0;
      const kickShift = (audio.beatPulse * 260.0 - audio.bass * 140.0) * gain;
      effectiveFocus = THREE.MathUtils.clamp(effectiveFocus + rhythmicOscillation + kickShift, 200, 1500);
    }
    this.material.uniforms.uFocusDistance.value = effectiveFocus;
    this.material.uniforms.uDofBlur.value = state.dofBlur ?? 0.45;
    this.material.uniforms.uDofInvert.value = state.dofInvert ? 1 : 0;
    this.material.uniforms.uDofAudioReact.value = state.dofAudioReact ? 1 : 0;
    this.material.uniforms.uShockwaveTime.value = this._shockwaveTime ?? -999;
    this.material.uniforms.uShockwaveStrength.value = this._shockwaveStrength ?? 0;
    this.material.uniforms.uAudioSub.value = applyGain(audio.sub);
    this.material.uniforms.uAudioBass.value = applyGain(audio.bass);
    this.material.uniforms.uAudioLowMid.value = applyGain(audio.lowMid);
    this.material.uniforms.uAudioHighMid.value = applyGain(audio.highMid);
    this.material.uniforms.uAudioTreble.value = applyGain(audio.treble);

    // MIDI mod wheel (CC7) drives whichever effect was toggled most recently - same "auto target"
    // spirit as applyMidiControlToSelectedTarget()/autoMidiTargetId() in NEU.pde.
    const midi = state.midiValue;
    if (state.lastToggledEffect === "gravity") vUniforms.uGravityStrength.value = 20 + midi * 160;
    if (state.lastToggledEffect === "fireball") {
      vUniforms.uFireballIntensity.value = 10 + midi * 140;
      vUniforms.uFireballTurbulence.value = 5 + midi * 110;
    }
    if (state.lastToggledEffect === "ring") vUniforms.uRingIntensity.value = 8 + midi * 140;
    if (state.lastToggledEffect === "ripple") vUniforms.uRippleStrength.value = 10 + midi * 160;

    // Restored audio-reactivity for the number-row effects that used to be static in the port:
    // Pattern Formation (Taste 9) ring radius/pull now breathes with the real combined signal,
    // like applyPatternFormation()'s signal*patternFormationIntensity term.
    vUniforms.uPatternStrength.value = 30 + combinedSignal * 110;
    // Shape Change (Taste 0) base spring pull also gets a light combined-signal boost; the actual
    // per-group low/mid/high band split now happens per-particle in the vertex shader (point size).
    vUniforms.uShapeChangeStrength.value = 45 + combinedSignal * 60;

    if (state.rippleOn) {
      if (this._ripplePhaseTime < 0) this._ripplePhaseTime = this.clockTime;
      const elapsed = this.clockTime - this._ripplePhaseTime;
      const period = 1.4;
      const phase = elapsed % period;
      vUniforms.uRippleRadius.value = phase * (BOUNDS_RADIUS * 1.35 / period);
    } else {
      this._ripplePhaseTime = -999;
    }

    const shockwaveAudioWeight = this._getSmoothedWeight("shockwaveAudioOn", dt);
    vUniforms.uShockwaveAudioOn.value = shockwaveAudioWeight;
    this.material.uniforms.uShockwaveAudioOn.value = shockwaveAudioWeight;

    let isMorphing = state.morphOn || state.objMorphOn || (state.brandMorphOn && !!this._brandTargets);

    // Track the morph on/off transition against the simulation clock.
    if (isMorphing !== this._prevMorphing) {
      this._prevMorphing = isMorphing;
      this._morphSwitchTime = this.clockTime;
    }
    let elapsedMorph = Math.max(0, this.clockTime - this._morphSwitchTime);

    // Intro sequence overrides the morph uniforms while it runs (and only if the user has not
    // triggered a morph himself).
    const intro = this._updateIntro(isMorphing);
    if (intro) {
      isMorphing = intro.on;
      elapsedMorph = intro.elapsed;
    }

    // Dynamic morph target texture selection (STARCODE text vs STUDIOPESC.COM branding vs 3D OBJ model)
    const activeMorph = (intro && intro.on) ? "starcode" : state.morphOn ? "starcode" : state.brandMorphOn ? "brand" : state.objMorphOn ? "obj" : "none";
    if (activeMorph !== this._currentMorphType) {
      if (activeMorph === "starcode" && this._starcodeTargets) {
        this.morphTargetTexture.image.data.set(this._starcodeTargets);
        this.morphTargetTexture.needsUpdate = true;
      } else if (activeMorph === "brand" && this._brandTargets) {
        this.morphTargetTexture.image.data.set(this._brandTargets);
        this.morphTargetTexture.needsUpdate = true;
      } else if (activeMorph === "obj") {
        const objTarget = this._customObjTargets || this._objTargets || buildDefault3DMeshTargets(MAX_PARTICLE_COUNT);
        this.morphTargetTexture.image.data.set(objTarget);
        this.morphTargetTexture.needsUpdate = true;
      }
      this._currentMorphType = activeMorph;
    }

    vUniforms.uMorphOn.value = isMorphing ? 1 : 0;
    vUniforms.uMorphElapsed.value = elapsedMorph;

    this.gpuCompute.compute();

    const posTex = this.gpuCompute.getCurrentRenderTarget(this.positionVariable).texture;
    const velTex = this.gpuCompute.getCurrentRenderTarget(this.velocityVariable).texture;
    this.material.uniforms.texturePosition.value = posTex;
    this.material.uniforms.textureVelocity.value = velTex;
    this.material.uniforms.uMorphOn.value = isMorphing ? 1 : 0;
    this.material.uniforms.uMorphElapsed.value = elapsedMorph;
    this.material.uniforms.uAudioLow.value = aLow;
    this.material.uniforms.uAudioMid.value = aMid;
    this.material.uniforms.uAudioHigh.value = aHigh;
    this.material.uniforms.uTime.value = this.clockTime;
    this.material.uniforms.uSizeMultiplier.value = state.sizeMultiplier;

    pUniforms.uDelta.value = dt;
    pUniforms.uSlowMotion.value = slow;
  }

  getSwarmCenter() {
    if (!this.gpuCompute || !this.positionVariable || !this.renderer) {
      return this._swarmCenter || new THREE.Vector3(0, 0, 0);
    }
    const renderTarget = this.gpuCompute.getCurrentRenderTarget(this.positionVariable);
    if (!renderTarget) return this._swarmCenter;

    if (!this._readBuffer) {
      this._readBuffer = new Float32Array(16 * 16 * 4);
    }
    try {
      this.renderer.readRenderTargetPixels(renderTarget, 0, 0, 16, 16, this._readBuffer);
      let sx = 0, sy = 0, sz = 0, count = 0;
      for (let i = 0; i < 256; i++) {
        const x = this._readBuffer[i * 4 + 0];
        const y = this._readBuffer[i * 4 + 1];
        const z = this._readBuffer[i * 4 + 2];
        if (!isNaN(x) && !isNaN(y) && !isNaN(z) && Math.abs(x) < 4000 && Math.abs(y) < 4000 && Math.abs(z) < 4000) {
          sx += x;
          sy += y;
          sz += z;
          count++;
        }
      }
      if (count > 0) {
        const targetCenter = new THREE.Vector3(sx / count, sy / count, sz / count);
        this._swarmCenter.lerp(targetCenter, 0.08);
      }
    } catch (e) {
      // ignore
    }
    return this._swarmCenter;
  }

  getSwarmBounds() {
    if (!this.gpuCompute || !this.positionVariable || !this.renderer) {
      return { center: this._swarmCenter.clone(), radius: BOUNDS_RADIUS };
    }

    const renderTarget = this.gpuCompute.getCurrentRenderTarget(this.positionVariable);
    if (!renderTarget) return { center: this._swarmCenter.clone(), radius: BOUNDS_RADIUS };

    const pixelCount = MAX_TEX_SIZE * MAX_TEX_SIZE;
    if (!this._boundsReadBuffer) this._boundsReadBuffer = new Float32Array(pixelCount * 4);

    try {
      this.renderer.readRenderTargetPixels(
        renderTarget,
        0,
        0,
        MAX_TEX_SIZE,
        MAX_TEX_SIZE,
        this._boundsReadBuffer
      );

      const limit = Math.min(this.activeParticleCount, pixelCount);
      let sx = 0;
      let sy = 0;
      let sz = 0;
      let count = 0;
      for (let i = 0; i < limit; i++) {
        const offset = i * 4;
        const x = this._boundsReadBuffer[offset];
        const y = this._boundsReadBuffer[offset + 1];
        const z = this._boundsReadBuffer[offset + 2];
        if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)
          && Math.abs(x) < 12000 && Math.abs(y) < 12000 && Math.abs(z) < 12000) {
          sx += x;
          sy += y;
          sz += z;
          count++;
        }
      }

      if (count === 0) return { center: this._swarmCenter.clone(), radius: BOUNDS_RADIUS };

      const center = new THREE.Vector3(sx / count, sy / count, sz / count);
      const distances = [];
      const sampleStep = Math.max(1, Math.floor(limit / 8192));
      for (let i = 0; i < limit; i += sampleStep) {
        const offset = i * 4;
        const x = this._boundsReadBuffer[offset];
        const y = this._boundsReadBuffer[offset + 1];
        const z = this._boundsReadBuffer[offset + 2];
        if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)
          && Math.abs(x) < 12000 && Math.abs(y) < 12000 && Math.abs(z) < 12000) {
          distances.push(Math.hypot(x - center.x, y - center.y, z - center.z));
        }
      }
      distances.sort((a, b) => a - b);
      const radius = distances[Math.min(distances.length - 1, Math.floor(distances.length * 0.9))] || BOUNDS_RADIUS;
      this._swarmCenter.copy(center);
      return { center, radius: THREE.MathUtils.clamp(radius, 80, 2400) };
    } catch (error) {
      console.warn("[STARCODE] Swarm bounds readback failed:", error);
      return { center: this._swarmCenter.clone(), radius: BOUNDS_RADIUS };
    }
  }
}
